/**
 * TemplateModals — Choose template, Save as template, Attach from library, Preview.
 *
 * Phase A7 spec:
 * - Choose template: start from saved template (header, areas, accessories, notes)
 * - Save as template: save current state (NOT customer-specific data)
 * - Attach from library: pull from Documents repo
 * - Preview: full PDF preview before send
 */

import React, { useState } from 'react';
import Slideover from '../../ui/Slideover';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { toDisplayString } from '../../../utils/displayString';
import { sanitizeTcHtml } from '../../../utils/sanitizeTcHtml';

// ─── Choose Template Modal ───────────────────────────────────────────────────

export function ChooseTemplateModal({ open, onClose, templates = [], onSelect }) {
  const [search, setSearch] = useState('');

  // PR-11 FIX (2026-07-22): route through toDisplayString so a poisoned-row
  // shape ({ name: { name, description }, ... }) still filters correctly
  // instead of stringifying to "[object Object]" and never matching search.
  const filtered = templates.filter((t) =>
    toDisplayString(t?.name).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Slideover open={open} onClose={onClose} title="Choose Template" subtitle="Start from a saved template" width="md">
      <div className="space-y-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates…"
        />

        {filtered.length === 0 && (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            {templates.length === 0 ? 'No templates saved yet.' : 'No templates match your search.'}
          </p>
        )}

        <div className="space-y-1">
          {filtered.map((template) => {
            // PT-12: Compute real area/item counts from template's line_items array
            const lineItems = Array.isArray(template.line_items) ? template.line_items : [];
            const areaSet = new Set(lineItems.map(li => li.areaId || li.area_id).filter(Boolean));
            const areaCount = areaSet.size || template.areaCount || 0;
            const itemCount = lineItems.length || template.itemCount || 0;
            return (
              <button
                key={template.id}
                type="button"
                className="w-full text-left px-4 py-3 rounded-md border border-[color:var(--color-border)] transition-colors hover:border-[color:var(--color-border-hover)] hover:shadow-sm"
                onClick={() => { onSelect(template); onClose(); }}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {toDisplayString(template.name, '(unnamed)')}
                </p>
                {template.description && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                    {template.description}
                  </p>
                )}
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {areaCount} area{areaCount !== 1 ? 's' : ''} · {itemCount} item{itemCount !== 1 ? 's' : ''}
                  {template.updatedAt && ` · Updated ${new Date(template.updatedAt).toLocaleDateString()}`}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </Slideover>
  );
}

// ─── Choose T&C Template Modal ───────────────────────────────────────────────
// PT-15 #5 (2026-07-24): closes the T&C-selector stub. Pre-fix, the "Change"
// button on the T&C card fetched settingsAPI.getTCTemplates() and then
// discarded the result — both branches opened the same info Dialog. This
// component mirrors ChooseTemplateModal's Slideover pattern but reads from
// the tenant's T&C templates (Settings → Quote Defaults → T&C Templates).
// Rule 0: options come from central Settings; selection persists via
// `metadata.tcTemplateId` on the quote row so QuoteReviewSend can render
// the chosen terms on the customer document.

export function ChooseTCTemplateModal({ open, onClose, templates = [], onSelect, currentId = null }) {
  const [search, setSearch] = useState('');

  const filtered = templates.filter((t) =>
    toDisplayString(t?.name).toLowerCase().includes(search.toLowerCase())
  );

  const emptyMessage = templates.length === 0
    ? 'No T&C templates configured yet. Go to Settings → Quote Defaults → T&C Templates to create one.'
    : 'No templates match your search.';

  return (
    <Slideover
      open={open}
      onClose={onClose}
      title="Choose T&C Template"
      subtitle="Pick the terms & conditions that apply to this quote"
      width="md"
    >
      <div className="space-y-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates…"
        />

        {filtered.length === 0 && (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            {emptyMessage}
          </p>
        )}

        <div className="space-y-1">
          {filtered.map((template) => {
            const isCurrent = currentId && template.id === currentId;
            // §6 (2026-08-08): T&C body is HTML. Strip tags via textContent
            // rather than regex — the stored HTML is sometimes malformed
            // (e.g. `<br></br>`), which naive tag-strip regexes leave visible.
            const raw = template.body || template.content || template.text || '';
            const preview = (() => {
              if (typeof raw !== 'string' || !raw) return '';
              // Parse as HTML fragment; textContent gives clean plain text.
              try {
                if (typeof document !== 'undefined') {
                  const div = document.createElement('div');
                  // IDI-624: cleaned first — an <img onerror> fires even in a
                  // detached div created by the live document.
                  div.innerHTML = sanitizeTcHtml(raw);
                  const text = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
                  return text.slice(0, 120);
                }
              } catch (e) { /* fall through */ }
              // SSR / no-DOM fallback: strip tags with a regex that tolerates
              // malformed pairs like `<br></br>` and self-closing `<br/>`.
              return raw.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 120);
            })();
            return (
              <button
                key={template.id}
                type="button"
                className="w-full text-left px-4 py-3 rounded-md border transition-colors hover:border-[color:var(--color-border-hover)]"
                style={{
                  borderColor: isCurrent ? 'var(--red-300)' : 'var(--color-border)',
                  backgroundColor: isCurrent ? 'var(--red-50)' : 'var(--color-surface)',
                }}
                onClick={() => { onSelect(template); onClose(); }}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {toDisplayString(template.name, '(unnamed)')}
                  {isCurrent && (
                    <span className="text-xs ml-2 font-normal" style={{ color: 'var(--color-text-secondary)' }}>
                      (current)
                    </span>
                  )}
                </p>
                {template.description && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                    {template.description}
                  </p>
                )}
                {preview && (
                  <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {preview}{raw.length > 120 ? '…' : ''}
                  </p>
                )}
                {template.updated_at && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Updated {new Date(template.updated_at).toLocaleDateString()}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Slideover>
  );
}

// ─── Save As Template Modal ──────────────────────────────────────────────────

export function SaveTemplateModal({ open, onClose, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim() });
    setName('');
    setDescription('');
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end w-full">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={handleSave} disabled={!name.trim()}>Save Template</Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={onClose} title="Save as Template" subtitle="Save current quote structure as a reusable template" footer={footer} width="sm">
      <div className="space-y-4">
        <div className="card p-3 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Templates capture: areas, line items, accessories, and notes.
            <br />
            Customer-specific data (name, contact, addresses) is NOT saved.
          </p>
        </div>

        <Input
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Standard Residential Insulation"
          required
        />

        <div>
          <label className="label mb-1">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-[color:var(--color-border)] px-3 py-2 text-sm resize-y"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            placeholder="Brief description of what this template includes…"
          />
        </div>
      </div>
    </Slideover>
  );
}

// ─── Attach From Library Modal ───────────────────────────────────────────────

export function AttachFromLibraryModal({ open, onClose, documents = [], onAttach }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  const filtered = documents.filter((d) =>
    String(d?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggleDoc = (doc) => {
    setSelected((prev) =>
      prev.some((s) => s.id === doc.id)
        ? prev.filter((s) => s.id !== doc.id)
        : [...prev, doc]
    );
  };

  const handleAttach = () => {
    onAttach(selected);
    setSelected([]);
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end w-full">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={handleAttach} disabled={selected.length === 0}>
        Attach {selected.length} Document{selected.length !== 1 ? 's' : ''}
      </Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={onClose} title="Attach from Library" subtitle="Pull files from your Files repo" footer={footer} width="md">
      <div className="space-y-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search files…"
        />

        {filtered.length === 0 && (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            {documents.length === 0 ? 'No documents in library.' : 'No documents match your search.'}
          </p>
        )}

        <div className="space-y-1">
          {filtered.map((doc) => {
            const isSelected = selected.some((s) => s.id === doc.id);
            return (
              <button
                key={doc.id}
                type="button"
                className="w-full text-left px-4 py-2.5 rounded-md border border-[color:var(--color-border)] transition-colors flex items-center gap-3"
                style={{
                  borderColor: isSelected ? 'var(--red-300)' : 'var(--color-border)',
                  backgroundColor: isSelected ? 'var(--red-50)' : 'var(--color-surface)',
                }}
                onClick={() => toggleDoc(doc)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  className="rounded border-[color:var(--color-border)] text-red-600 focus:ring-red-500"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>{doc.name}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {doc.type || 'Document'} · {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ''}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Slideover>
  );
}

// ─── Preview Modal ───────────────────────────────────────────────────────────

export function PreviewModal({ open, onClose, quoteData, onSend }) {
  return (
    <Slideover open={open} onClose={onClose} title="Quote Preview" subtitle="Full preview before sending" width="xl">
      <div className="space-y-4">
        {/* PDF preview placeholder */}
        <div
          className="card min-h-[600px] flex items-center justify-center"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}
        >
          <div className="text-center space-y-2">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              PDF Preview
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Full quote document will render here
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {onSend && <Button onClick={onSend}>Continue to Send</Button>}
        </div>
      </div>
    </Slideover>
  );
}
